import { generatePath } from "react-router-dom";

/**
 * Extracts the param names from a path string into a typed params object.
 *
 * `/foo/:bar/:baz` -> `{ bar: string; baz: string }`
 * `/foo` -> `Record<string, never>`
 */
type ParamsOf<P extends string> = P extends `${string}:${infer Param}/${infer Rest}`
  ? { [K in Param | keyof ParamsOf<`/${Rest}`>]: string }
  : P extends `${string}:${infer Param}`
    ? { [K in Param]: string }
    : Record<string, never>;

/**
 * Pairs a route's path string with a typed URL builder so the two can't drift.
 *
 * The same `path` feeds both the `RouteObject` in the route tree and the `url(...)` builder
 * used by callers, and `url`'s params are checked against the `:params` in the path at compile time.
 */
export function defineRoute<P extends string>(path: P) {
  return {
    path,
    url: (params: ParamsOf<P>, searchParams: Record<string, string | undefined> = {}) => {
      const search = new URLSearchParams();
      for (const [key, value] of Object.entries(searchParams)) {
        if (value) search.set(key, value);
      }
      const pathname = generatePath(path, params);
      return search.size ? `${pathname}?${search}` : pathname;
    },
  };
}
